import { Tornado, BadgeCheck, Hourglass, EyeOff, BellRing, BellOff, Ruler, Satellite } from "lucide-react";
import { HAZARD_BASE, HAZARD_LABEL } from "@/lib/hazard";
import { hrs } from "@/lib/clipboard";

const BAND_TIP = "Alert bands — below 70%: internal only · 70–74%: listed, not alerted · 75%+: listed and alerted. Rule alerts (rain watch/warning, radar river blockage) trip at 70%+.";
const SRC = { ai: "AI", calibrated: "Calibrated", rule: "Rule" };

const BandChip = ({ inc }) => {
  const pct = inc.conf?.value != null ? ` · ${SRC[inc.conf.source] || "AI"} ${Math.round(inc.conf.value * 100)}%` : "";
  if (inc.band === "rule_alert")
    return <Chip cls="border-atlas-orange/50 text-atlas-orange" testid={`incident-band-${inc.id}`} title={BAND_TIP}><Ruler className="w-3 h-3" />Rule alert{pct}</Chip>;
  if (inc.band === "alerted")
    return <Chip cls="border-atlas-red/50 text-atlas-red" testid={`incident-band-${inc.id}`} title={BAND_TIP}><BellRing className="w-3 h-3" />Alerted{pct}</Chip>;
  if (inc.band === "listed")
    return <Chip cls="border-white/20 text-atlas-secondary" testid={`incident-band-${inc.id}`} title={BAND_TIP}><BellOff className="w-3 h-3" />Listed · below 75% alert line{pct}</Chip>;
  return null;
};

const Chip = ({ cls, children, testid, title }) => (
  <span className={`inline-flex items-center gap-1 px-1.5 h-5 border text-[9px] font-mono uppercase tracking-wider ${cls}`} data-testid={testid} title={title}>
    {children}
  </span>
);

// Hazard tags + storm link + confirmed lead time + lifecycle, shared by single- and multi-site incidents.
export const IncidentBadges = ({ inc }) => {
  const exp = inc.lead.feed?.expires_in_h;
  return (
    <div className="flex flex-wrap items-center gap-1.5 my-1.5" data-testid={`incident-badges-${inc.id}`}>
      <BandChip inc={inc} />
      {inc.hazards.map((h) => (
        <Chip key={h} cls="border-white/15 text-white/80" testid={`incident-hazard-${inc.id}-${h}`}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: HAZARD_BASE[h] }} />{HAZARD_LABEL[h] || h}
        </Chip>
      ))}
      {inc.storm && (
        <Chip cls="border-sky-400/50 text-sky-300" testid={`incident-storm-${inc.id}`}
          title={`${inc.storm.label} (NOAA NHC) is ${inc.storm.distance_km} km away — inside its ${inc.storm.radius_km} km rainfall influence radius`}>
          <Tornado className="w-3 h-3" />{inc.storm.label} · {inc.storm.distance_km} km
        </Chip>
      )}
      {inc.confirmed && (
        <Chip cls="border-emerald-400/50 text-emerald-300" testid={`incident-confirmed-${inc.id}`}
          title={inc.confirmed.title || "Confirmed by an agency report"}>
          <BadgeCheck className="w-3 h-3" />Confirmed · {inc.confirmed.lead_hours != null ? `${hrs(inc.confirmed.lead_hours)} ahead of ${inc.confirmed.source || "agency"}` : inc.confirmed.source}
        </Chip>
      )}
      {inc.sarVerified && (
        <Chip cls="border-violet-400/40 text-violet-300" testid={`incident-sar-${inc.id}`}
          title="Vigilo Earth's own Sentinel-1 radar analysis. An input signal only — never counts as an agency confirmation.">
          <Satellite className="w-3 h-3" />Satellite-verified (Vigilo Earth SAR analysis)
        </Chip>
      )}
      {inc.tier === "monitoring" && inc.reasons.length > 0 && (
        <Chip cls="border-white/10 text-atlas-secondary" testid={`incident-monitoring-${inc.id}`} title="Below the main-feed threshold">
          <EyeOff className="w-3 h-3" />{inc.reasons[0]}
        </Chip>
      )}
      {exp != null && exp <= 24 && (
        <Chip cls="border-amber-400/40 text-amber-300" testid={`incident-expiring-${inc.id}`} title="Closes automatically at 96 h unless confirmed or escalated">
          <Hourglass className="w-3 h-3" />closes in {hrs(exp)}
        </Chip>
      )}
    </div>
  );
};
